/**
 * Listing lifecycle hard-gate evaluator (Phase 4 shadow).
 * Pure — no status mutation. Price-in-band uses admin suitability semantics.
 */

import type { AdminPriceSuitabilityStatus } from "@/lib/server/adminPriceSuitability";
import {
  evaluateListingFreeTextHardGate,
  type ListingFreeTextHardGateResult,
} from "@/lib/listing-lifecycle/listingFreeTextHardGate";
import {
  getRequirementsForSubcategory,
  LISTING_SPEC_ENABLED_WAVES,
  validateAttributesForSubcategory,
  type ListingSpecAttributes,
} from "@/lib/listing/specRequirements";
import {
  evaluateListingPhotoUniqueness,
  type ListingPhotoUniquenessResult,
} from "@/lib/listing/listingPhotoDuplicateCheck";

/** Sell flow requires exactly 3; hard gate requires at least this many. */
export const MIN_LISTING_PHOTOS_FOR_HARD_GATE = 3;

export const LISTING_HARD_GATE_KEYS = [
  "photos",
  "photo_uniqueness",
  "category_spec",
  "price_in_band",
  "free_text",
  "prohibited",
] as const;

export type ListingHardGateKey = (typeof LISTING_HARD_GATE_KEYS)[number];

export type ListingHardGateOutcome = {
  key: ListingHardGateKey;
  pass: boolean;
  detail: string | null;
};

export type ListingHardGatesResult = {
  /** True only when every v1 hard gate passes (allowlist is Phase 5). */
  pass: boolean;
  gates: Record<ListingHardGateKey, ListingHardGateOutcome>;
  evaluatedAt: string;
  /** True when price suitability was not supplied (shadow still records a fail). */
  pricePending: boolean;
  freeText: ListingFreeTextHardGateResult;
};

export type EvaluateListingHardGatesInput = {
  photoCount: number;
  subcategoryId: string | null | undefined;
  attributes?: ListingSpecAttributes | null;
  /**
   * Admin price suitability. `null` = not computed yet (pricePending).
   * Aligns with seller ceiling-only enforcement: `in_band` and `below_band`
   * pass; `above_band` / `no_band` / `skipped` fail.
   */
  priceSuitability: AdminPriceSuitabilityStatus | null;
  title: string;
  description?: string | null;
  defects?: string | null;
  /** Future prohibited / AI flags — true forces fail. */
  prohibitedFlags?: boolean;
  /**
   * Per-slot SHA-256 from `item_images.image_hash` (position order).
   * Omit → gate passes with legacy-unknown detail (admin re-eval when images load).
   */
  imageContentHashes?: readonly (string | null | undefined)[];
  /**
   * When false, null/missing hashes fail the gate (auto-approve path).
   * Default true — legacy listings without backfill stay human-reviewable.
   */
  photoUniquenessLegacyNullPasses?: boolean;
  nowIso?: string;
};

function evaluatePhotosGate(photoCount: number): ListingHardGateOutcome {
  const pass = photoCount >= MIN_LISTING_PHOTOS_FOR_HARD_GATE;
  return {
    key: "photos",
    pass,
    detail: pass
      ? `${photoCount} photos`
      : `Need ${MIN_LISTING_PHOTOS_FOR_HARD_GATE} photos (have ${photoCount})`,
  };
}

function evaluateCategorySpecGate(
  subcategoryId: string | null | undefined,
  attributes: ListingSpecAttributes | null | undefined,
): ListingHardGateOutcome {
  const requirement = getRequirementsForSubcategory(subcategoryId);
  if (!requirement) {
    return {
      key: "category_spec",
      pass: true,
      detail: "No Wave spec profile",
    };
  }

  if (!LISTING_SPEC_ENABLED_WAVES.includes(requirement.wave)) {
    return {
      key: "category_spec",
      pass: true,
      detail: `${requirement.wave} not enforced`,
    };
  }

  const validation = validateAttributesForSubcategory(
    subcategoryId,
    attributes,
    { enforceRequired: true },
  );
  if (validation.isValid) {
    return {
      key: "category_spec",
      pass: true,
      detail: `${requirement.wave} specs ok`,
    };
  }

  const first = validation.issues[0]?.message ?? "Missing category specs";
  return {
    key: "category_spec",
    pass: false,
    detail: first,
  };
}

function evaluatePriceInBandGate(
  priceSuitability: AdminPriceSuitabilityStatus | null,
): { outcome: ListingHardGateOutcome; pricePending: boolean } {
  if (priceSuitability === null) {
    return {
      pricePending: true,
      outcome: {
        key: "price_in_band",
        pass: false,
        detail: "Price band not evaluated yet",
      },
    };
  }

  // Ceiling-only parity with sell: below band is an allowed faster-sale price.
  if (priceSuitability === "in_band" || priceSuitability === "below_band") {
    return {
      pricePending: false,
      outcome: {
        key: "price_in_band",
        pass: true,
        detail: priceSuitability,
      },
    };
  }

  return {
    pricePending: false,
    outcome: {
      key: "price_in_band",
      pass: false,
      detail: priceSuitability,
    },
  };
}

function evaluatePhotoUniquenessGate(
  photoCount: number,
  imageContentHashes: readonly (string | null | undefined)[] | undefined,
  legacyNullPasses: boolean,
): ListingHardGateOutcome {
  if (photoCount < MIN_LISTING_PHOTOS_FOR_HARD_GATE) {
    return {
      key: "photo_uniqueness",
      pass: true,
      detail: "Needs photo count gate first",
    };
  }

  if (!imageContentHashes || imageContentHashes.length === 0) {
    return {
      key: "photo_uniqueness",
      pass: legacyNullPasses,
      detail: legacyNullPasses
        ? "Fingerprints not evaluated"
        : "Photo fingerprints pending",
    };
  }

  const uniqueness: ListingPhotoUniquenessResult =
    evaluateListingPhotoUniqueness(imageContentHashes, { legacyNullPasses });

  return {
    key: "photo_uniqueness",
    pass: uniqueness.pass,
    detail: uniqueness.pass
      ? uniqueness.unknownLegacy
        ? (uniqueness.detail ?? "Fingerprints pending")
        : "3 distinct photos"
      : (uniqueness.detail ?? "Duplicate photos"),
  };
}

function evaluateProhibitedGate(
  prohibitedFlags: boolean,
): ListingHardGateOutcome {
  return {
    key: "prohibited",
    pass: !prohibitedFlags,
    detail: prohibitedFlags ? "Prohibited content flag" : null,
  };
}

/**
 * Evaluate all v1 hard gates for shadow / future auto-approve.
 * Does not change listing status.
 */
export function evaluateListingHardGates(
  input: EvaluateListingHardGatesInput,
): ListingHardGatesResult {
  const freeText = evaluateListingFreeTextHardGate({
    title: input.title,
    description: input.description,
    defects: input.defects,
  });

  const photos = evaluatePhotosGate(input.photoCount);
  const photo_uniqueness = evaluatePhotoUniquenessGate(
    input.photoCount,
    input.imageContentHashes,
    input.photoUniquenessLegacyNullPasses ?? true,
  );
  const category_spec = evaluateCategorySpecGate(
    input.subcategoryId,
    input.attributes,
  );
  const { outcome: price_in_band, pricePending } = evaluatePriceInBandGate(
    input.priceSuitability,
  );
  const free_text: ListingHardGateOutcome = {
    key: "free_text",
    pass: freeText.pass,
    detail: freeText.pass
      ? null
      : (freeText.summary ?? freeText.codes.join(", ")),
  };
  const prohibited = evaluateProhibitedGate(Boolean(input.prohibitedFlags));

  const gates: Record<ListingHardGateKey, ListingHardGateOutcome> = {
    photos,
    photo_uniqueness,
    category_spec,
    price_in_band,
    free_text,
    prohibited,
  };

  const pass = LISTING_HARD_GATE_KEYS.every((key) => gates[key].pass);

  return {
    pass,
    gates,
    evaluatedAt: input.nowIso ?? new Date().toISOString(),
    pricePending,
    freeText,
  };
}

/** Compact admin tooltip / column summary (e.g. "✓✓○✓✓"). */
export function formatListingHardGatesGlyph(
  result: ListingHardGatesResult,
): string {
  return LISTING_HARD_GATE_KEYS.map((key) =>
    result.gates[key].pass ? "✓" : "○",
  ).join("");
}

/** Plain-language breakdown for tooltips. */
export function formatListingHardGatesTooltip(
  result: ListingHardGatesResult,
): string {
  return LISTING_HARD_GATE_KEYS.map((key) => {
    const g = result.gates[key];
    const mark = g.pass ? "✓" : "○";
    return `${key} ${mark}${g.detail ? ` (${g.detail})` : ""}`;
  }).join(" · ");
}

export function isListingHardGateBreakdown(
  value: unknown,
): value is ListingHardGatesResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<ListingHardGatesResult>;
  return (
    typeof v.pass === "boolean" &&
    typeof v.evaluatedAt === "string" &&
    typeof v.pricePending === "boolean" &&
    v.gates !== undefined &&
    typeof v.gates === "object"
  );
}
